import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import restaurantService, { CatalogItem, MenuItem, Restaurant } from '../../services/restaurantService';
import cartService, { CartItem, CartOptions } from '../../services/cartService';
import MenuItemOptionsSheet from '../common/MenuItemOptionsSheet/MenuItemOptionsSheet';
import {
  getInitialSelections,
  MenuOrderSelections,
  validateOrderSelections,
} from '../common/MenuOrderOptions/MenuOrderOptions';
import {
  getEffectiveDisplayPrice,
  getMenuOptionsMeta,
} from '../../utils/menuPricing';
import { DEFAULT_IMAGES, getMenuItemImageUrl } from '../../constants/images';
import { CART_UPDATED_EVENT } from '../../utils/cartEvents';
import { useGuestOrderType } from '../../hooks/useGuestOrderType';
import { useGuestCanOrder } from '../../hooks/useGuestCanOrder';
import {
  GuestOrderingStatus,
  guestOrderingMessage,
  isAcceptingGuestOrders,
} from '../../utils/guestOrdering';
import MobileMenuFilterOverlay, {
  MenuFilterCategory,
  MenuFilterCuisine,
} from '../RestaurantMenu/MobileMenuFilterOverlay';
import '../RestaurantMenu/RestaurantMenu.css';
import { kioskMenuPath } from './kioskPaths';
import styles from './KioskMenu.module.css';

function toCatalogItem(entry: {
  id: number;
  type?: string;
  name?: string;
  iconUrl?: string;
  displayOrder?: number;
}): CatalogItem {
  return {
    id: entry.id,
    name: entry.name || entry.type || '',
    type: entry.type,
    iconUrl: entry.iconUrl,
    displayOrder: entry.displayOrder,
  };
}

function itemImage(restaurant: Restaurant | null, item: MenuItem): string {
  if (restaurant?.id && item.id && item.imageBig) {
    return getMenuItemImageUrl(restaurant.id, item.id, item.imageBig);
  }
  return DEFAULT_IMAGES.MENU_ITEM;
}

function isBestseller(item: MenuItem): boolean {
  if (item.specialItem) return true;
  const tags = (item.tags || []).map((t) => t.toLowerCase());
  return tags.some((t) => t.includes('best') || t.includes('popular'));
}

function isSpicy(item: MenuItem): boolean {
  const tags = (item.tags || []).map((t) => t.toLowerCase());
  if (tags.some((t) => t.includes('spicy') || t.includes('chili') || t.includes('chilli'))) {
    return true;
  }
  const levels = item.spicinessLevels || [];
  return levels.some((l) => /hot|spicy|extra/i.test(String(l.levelName || l.levelCode || '')));
}

function spiceLabel(item: MenuItem): string | null {
  const levels = item.spicinessLevels || [];
  if (levels.length === 1) return String(levels[0].levelName || levels[0].levelCode || 'Spicy');
  if (isSpicy(item)) return 'Spicy';
  return null;
}

const KioskMenu: React.FC = () => {
  const { appName, itemUrl } = useParams<{ appName: string; itemUrl?: string }>();
  const navigate = useNavigate();
  const guestOrderType = useGuestOrderType(appName);
  const { canOrder } = useGuestCanOrder(appName);

  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [guestOrdering, setGuestOrdering] = useState<GuestOrderingStatus | null>(null);
  const [allItems, setAllItems] = useState<MenuItem[]>([]);
  const [visibleItems, setVisibleItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<CatalogItem[]>([]);
  const [filterCategories, setFilterCategories] = useState<MenuFilterCategory[]>([]);
  const [cuisines, setCuisines] = useState<MenuFilterCuisine[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null); // null = All
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedCuisines, setSelectedCuisines] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [itemType, setItemType] = useState<string>('all');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [sortBy, setSortBy] = useState<string>('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cartCount, setCartCount] = useState(0);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [optionsItem, setOptionsItem] = useState<MenuItem | null>(null);
  const [selections, setSelections] = useState<MenuOrderSelections>({});
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [upsellItems, setUpsellItems] = useState<MenuItem[]>([]);

  const currency = restaurant?.currencySymbol || '₹';
  const formatPrice = useCallback(
    (price: number) => `${currency}${Math.round(price).toLocaleString('en-IN')}`,
    [currency]
  );

  const loadCartSummary = useCallback(async () => {
    if (!appName) return;
    try {
      const [countRes, cartRes] = await Promise.all([
        cartService.getCartCount(appName),
        cartService.getCart(appName, guestOrderType),
      ]);
      if (countRes.success) setCartCount(countRes.cartCount || 0);
      if (cartRes.success) {
        setCartItems(cartRes.cartItems || []);
      }
    } catch {
      /* ignore */
    }
  }, [appName, guestOrderType]);

  useEffect(() => {
    if (!appName) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [response, restRes] = await Promise.all([
          restaurantService.getRestaurantMenu(appName),
          restaurantService.getRestaurant(appName),
        ]);
        if (cancelled) return;
        if (!response.success) {
          setError(response.message || 'Failed to load menu');
          return;
        }
        setRestaurant(response.restaurant || null);
        setGuestOrdering(restRes.guestOrdering ?? null);
        const items = response.menu?.menuList || [];
        setAllItems(items);
        setVisibleItems(items);
        const cats = (response.categories || []).map((c) => toCatalogItem(c));
        setCategories(cats);
        setFilterCategories(
          (response.categories || []).map((c) => ({
            id: c.id,
            type: c.type || c.name || '',
          }))
        );
        setCuisines(
          (response.cuisines || []).map((c) => ({
            id: c.id,
            name: c.name || '',
          }))
        );
        const allTags = new Set<string>();
        items.forEach((item) => {
          item.tags?.forEach((tag) => {
            if (tag?.trim()) allTags.add(tag);
          });
        });
        setTags(Array.from(allTags));
        const maxItemPrice = items.length > 0 ? Math.max(...items.map((i) => i.price || 0)) : 0;
        const sliderMax = Math.max(500, Math.ceil(maxItemPrice / 50) * 50);
        setPriceRange([0, sliderMax]);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load menu');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    loadCartSummary();
    return () => {
      cancelled = true;
    };
  }, [appName, loadCartSummary]);

  useEffect(() => {
    if (!appName) return undefined;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ appName: string; count?: number }>).detail;
      if (detail?.appName !== appName) return;
      if (typeof detail.count === 'number') setCartCount(detail.count);
      loadCartSummary();
    };
    window.addEventListener(CART_UPDATED_EVENT, handler);
    return () => window.removeEventListener(CART_UPDATED_EVENT, handler);
  }, [appName, loadCartSummary]);

  useEffect(() => {
    if (!itemUrl || !allItems.length) return;
    const match = allItems.find(
      (item) => item.itemUrl === itemUrl || String(item.id) === itemUrl
    );
    if (match && match.availability !== false) {
      setOptionsItem(match);
      setSelections(getInitialSelections(match));
      setOptionsError(null);
    }
  }, [itemUrl, allItems]);

  const priceSliderMax = useMemo(() => {
    if (allItems.length === 0) return 1000;
    const max = Math.max(...allItems.map((item) => item.price || 0));
    return Math.max(500, Math.ceil(max / 50) * 50);
  }, [allItems]);

  const categoryCounts = useMemo(() => {
    const map = new Map<number, number>();
    visibleItems.forEach((item) => {
      map.set(item.categoryId, (map.get(item.categoryId) || 0) + 1);
    });
    return map;
  }, [visibleItems]);

  const categoryThumb = useCallback(
    (cat: CatalogItem): string => {
      if (cat.iconUrl) return cat.iconUrl;
      const first = allItems.find((i) => i.categoryId === cat.id && i.imageBig);
      if (first) return itemImage(restaurant, first);
      return DEFAULT_IMAGES.MENU_ITEM;
    },
    [allItems, restaurant]
  );

  const qtyByMenuId = useMemo(() => {
    const map = new Map<number, { qty: number; cartId: number; sizeTypeId?: number }>();
    cartItems.forEach((ci) => {
      const menuId = ci.menuId || ci.menu?.id;
      if (!menuId) return;
      const prev = map.get(menuId);
      const qty = (prev?.qty || 0) + (ci.quantity || 0);
      map.set(menuId, {
        qty,
        cartId: ci.cartId,
        sizeTypeId: ci.sizeTypeId,
      });
    });
    return map;
  }, [cartItems]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (itemType !== 'all') n += 1;
    if (selectedCategories.length > 0) n += 1;
    if (selectedCuisines.length > 0) n += 1;
    if (selectedTags.length > 0) n += 1;
    if (sortBy !== 'default') n += 1;
    if (priceRange[1] < priceSliderMax) n += 1;
    return n;
  }, [
    itemType,
    selectedCategories.length,
    selectedCuisines.length,
    selectedTags.length,
    sortBy,
    priceRange,
    priceSliderMax,
  ]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return visibleItems.filter((item) => {
      if (selectedCategoryId != null && item.categoryId !== selectedCategoryId && !q) {
        return false;
      }
      if (q && !item.item.toLowerCase().includes(q) && !(item.description || '').toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [visibleItems, selectedCategoryId, searchQuery]);

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);

  const reloadFullMenu = useCallback(async () => {
    if (!appName) return;
    const response = await restaurantService.getRestaurantMenu(appName, sortBy);
    if (response.success) {
      const items = response.menu?.menuList || [];
      setAllItems(items);
      setVisibleItems(items);
    }
  }, [appName, sortBy]);

  const applyMenuFilter = useCallback(async () => {
    if (!appName) return;
    try {
      const priceFilterActive = priceRange[1] < priceSliderMax;
      const response = await restaurantService.filterMenu(appName, {
        itemType,
        categories: selectedCategories,
        cuisines: selectedCuisines,
        tags: selectedTags,
        priceFilterEnabled: priceFilterActive,
        minPrice: 0,
        maxPrice: priceRange[1],
        sortBy,
      });
      if (response.success) {
        setVisibleItems(response.menuItems || []);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error applying filter');
    }
  }, [
    appName,
    itemType,
    selectedCategories,
    selectedCuisines,
    selectedTags,
    priceRange,
    priceSliderMax,
    sortBy,
  ]);

  const clearMenuFilters = useCallback(() => {
    setSelectedCategories([]);
    setSelectedCuisines([]);
    setSelectedTags([]);
    setItemType('all');
    setPriceRange([0, priceSliderMax]);
    setSortBy('default');
    void reloadFullMenu();
  }, [priceSliderMax, reloadFullMenu]);

  const handleCategoryToggle = (categoryId: number) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const handleCuisineToggle = (cuisineId: number) => {
    setSelectedCuisines((prev) =>
      prev.includes(cuisineId) ? prev.filter((id) => id !== cuisineId) : [...prev, cuisineId]
    );
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleItemTypeToggle = (type: 'veg' | 'nonveg') => {
    if (itemType === type) {
      setItemType('all');
    } else if (itemType === 'all') {
      setItemType(type);
    } else {
      setItemType('all');
    }
  };

  const showUpsell = (added: MenuItem) => {
    const otherCatItems = allItems.filter(
      (i) =>
        i.categoryId !== added.categoryId &&
        i.availability !== false &&
        i.id !== added.id
    );
    setUpsellItems(otherCatItems.slice(0, 4));
  };

  const addToCart = async (item: MenuItem, options?: CartOptions): Promise<boolean> => {
    if (!appName) return false;
    if (!isAcceptingGuestOrders(guestOrdering) || !canOrder) {
      setOptionsError(
        guestOrderingMessage(guestOrdering) ||
          'This restaurant is not accepting orders right now.'
      );
      return false;
    }
    try {
      const response = await cartService.addToCart(appName, item.id, 1, guestOrderType, options);
      if (response.success) {
        setCartCount(response.cartCount || 0);
        await loadCartSummary();
        showUpsell(item);
        if (itemUrl) {
          navigate(kioskMenuPath(appName), { replace: true });
        }
        return true;
      }
      setOptionsError(response.message || 'Could not add item');
      return false;
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not add item';
      setOptionsError(message);
      return false;
    }
  };

  const changeQty = async (item: MenuItem, nextQty: number) => {
    if (!appName) return;
    const entry = qtyByMenuId.get(item.id);
    if (!entry) {
      if (nextQty > 0) void openOptionsOrAdd(item);
      return;
    }
    try {
      if (nextQty <= 0) {
        await cartService.deleteCartItem(appName, entry.cartId, item.id);
        await loadCartSummary();
        return;
      }
      await cartService.updateCart(appName, [
        {
          menuId: item.id,
          quantity: nextQty,
          orderType: guestOrderType,
          sizeTypeId: entry.sizeTypeId,
        },
      ]);
      await loadCartSummary();
    } catch {
      /* ignore */
    }
  };

  const openOptionsOrAdd = (item: MenuItem, opts?: { inlineOptions?: boolean }) => {
    if (item.availability === false) return;
    const meta = getMenuOptionsMeta(item);
    if (meta.needsPicker) {
      setOptionsItem(item);
      setSelections(getInitialSelections(item));
      setOptionsError(null);
      if (!opts?.inlineOptions && appName) {
        navigate(kioskMenuPath(appName, item.itemUrl || String(item.id)));
      }
      return;
    }
    void addToCart(item);
  };

  const upsellQuickAdd = (item: MenuItem) => {
    setOptionsError(null);
    const meta = getMenuOptionsMeta(item);
    if (meta.needsPicker) {
      openOptionsOrAdd(item, { inlineOptions: true });
      return;
    }
    void addToCart(item).then((ok) => {
      if (ok) {
        setUpsellItems((prev) => prev.filter((i) => i.id !== item.id));
      }
    });
  };

  const confirmOptions = () => {
    if (!optionsItem) return;
    const err = validateOrderSelections(optionsItem, selections);
    if (err) {
      setOptionsError(err);
      return;
    }
    setOptionsError(null);
    void addToCart(optionsItem, { sizeTypeId: selections.sizeTypeId }).then((ok) => {
      if (ok) {
        setOptionsItem(null);
        setUpsellItems((prev) => prev.filter((i) => i.id !== optionsItem.id));
      }
    });
  };

  const closeOptions = () => {
    setOptionsItem(null);
    setOptionsError(null);
    if (appName && itemUrl) {
      navigate(kioskMenuPath(appName), { replace: true });
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading menu…</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <div className={styles.shell}>
      <section className={styles.searchStrip}>
        <div className={styles.searchRow}>
          <span className={`material-symbols-outlined ${styles.searchIcon}`}>search</span>
          <input
            className={styles.search}
            type="search"
            placeholder="Search dishes, curries, breads or spices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search menu"
          />
          <div className={styles.searchActions}>
            <button type="button" className={styles.filterBtn} onClick={() => setMobileFilterOpen(true)}>
              <span className="material-symbols-outlined" style={{ fontSize: 22 }}>
                tune
              </span>
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
          </div>
        </div>
      </section>

      <div className={styles.body}>
        <aside className={styles.rail} aria-label="Categories">
          <button
            type="button"
            className={`${styles.railBtn}${selectedCategoryId === null ? ` ${styles.railBtnActive}` : ''}`}
            onClick={() => {
              setSelectedCategoryId(null);
              setSearchQuery('');
            }}
          >
            <span className={styles.railThumb}>
              {restaurant?.appLogo ? (
                <img src={restaurant.appLogo} alt="" />
              ) : (
                <span className={`material-symbols-outlined ${styles.railAllIcon}`}>restaurant_menu</span>
              )}
            </span>
            <span className={styles.railMeta}>
              <span className={styles.railName}>All</span>
              <span className={styles.railCount}>{allItems.length}</span>
            </span>
          </button>
          {categories.map((cat) => {
            const count = categoryCounts.get(cat.id) || 0;
            const active = selectedCategoryId === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                className={`${styles.railBtn}${active ? ` ${styles.railBtnActive}` : ''}`}
                onClick={() => {
                  setSelectedCategoryId(cat.id);
                  setSearchQuery('');
                }}
              >
                <span className={styles.railThumb}>
                  <img
                    src={categoryThumb(cat)}
                    alt=""
                    onError={(e) => {
                      const t = e.target as HTMLImageElement;
                      if (t.src !== DEFAULT_IMAGES.MENU_ITEM) t.src = DEFAULT_IMAGES.MENU_ITEM;
                    }}
                  />
                </span>
                <span className={styles.railMeta}>
                  <span className={styles.railName}>{cat.name}</span>
                  <span className={styles.railCount}>{count}</span>
                </span>
              </button>
            );
          })}
        </aside>

        <div className={styles.main}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitle}>
              <h1>{selectedCategory?.name || 'All dishes'}</h1>
              <span className={styles.sectionCount}>· {filteredItems.length} items</span>
            </div>
          </div>

          {optionsError && !optionsItem ? (
            <p className={styles.menuToast} role="alert">
              {optionsError}
            </p>
          ) : null}

          {upsellItems.length > 0 && (
            <section className={styles.upsell} aria-label="Goes well with">
              <div className={styles.upsellHead}>
                <div className={styles.upsellTitle}>
                  <span className="material-symbols-outlined">auto_awesome</span>
                  Goes well with
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span className={styles.upsellFast}>Fast Add</span>
                  <button
                    type="button"
                    className={styles.upsellDismiss}
                    onClick={() => setUpsellItems([])}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
              <div className={styles.upsellRow}>
                {upsellItems.map((item) => (
                  <div key={item.id} className={styles.upsellCard}>
                    <div className={styles.upsellThumb}>
                      <img
                        src={itemImage(restaurant, item)}
                        alt=""
                        onError={(e) => {
                          const t = e.target as HTMLImageElement;
                          if (t.src !== DEFAULT_IMAGES.MENU_ITEM) t.src = DEFAULT_IMAGES.MENU_ITEM;
                        }}
                      />
                    </div>
                    <div className={styles.upsellMeta}>
                      <span className={styles.upsellName}>{item.item}</span>
                      <span className={styles.upsellPrice}>
                        {formatPrice(getEffectiveDisplayPrice(item))}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={styles.upsellAdd}
                      onClick={(e) => {
                        e.stopPropagation();
                        upsellQuickAdd(item);
                      }}
                    >
                      + Add
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className={styles.grid}>
            {filteredItems.map((item) => {
              const available = item.availability !== false;
              const imageSrc = itemImage(restaurant, item);
              const price = getEffectiveDisplayPrice(item);
              const qty = qtyByMenuId.get(item.id)?.qty || 0;
              const spice = spiceLabel(item);
              const bestseller = isBestseller(item);
              return (
                <article
                  key={item.id}
                  className={`${styles.card}${!available ? ` ${styles.cardDisabled}` : ''}`}
                >
                  <div>
                    <div className={styles.cardMedia}>
                      <img
                        src={imageSrc}
                        alt=""
                        className={styles.cardImg}
                        loading="lazy"
                        onError={(e) => {
                          const t = e.target as HTMLImageElement;
                          if (t.src !== DEFAULT_IMAGES.MENU_ITEM) t.src = DEFAULT_IMAGES.MENU_ITEM;
                        }}
                      />
                      <div className={styles.vegBadge} aria-hidden>
                        <span
                          className={styles.vegBadgeDot}
                          style={{
                            background: item.itemType === 'nonveg' ? '#DC2626' : '#16A34A',
                          }}
                        />
                      </div>
                      {bestseller && available ? (
                        <span className={styles.bestseller}>
                          <span className="material-symbols-outlined">stars</span>
                          BESTSELLER
                        </span>
                      ) : null}
                      {!available ? (
                        <div className={styles.soldOutOverlay}>
                          <span className={styles.soldOutBadge}>Sold Out</span>
                        </div>
                      ) : null}
                    </div>
                    <div className={styles.cardBody}>
                      <h2 className={styles.cardTitle}>{item.item}</h2>
                      {item.description ? (
                        <p className={styles.cardDesc}>{item.description}</p>
                      ) : null}
                      <div className={styles.metaRow}>
                        {spice ? (
                          <span className={styles.metaChip}>
                            <span className="material-symbols-outlined">local_fire_department</span>
                            {spice}
                          </span>
                        ) : null}
                        <span className={styles.metaChip}>Serves 1</span>
                        {bestseller ? (
                          <span className={`${styles.metaChip} ${styles.metaPopular}`}>Popular</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className={styles.cardFooter}>
                    <span className={styles.price}>{formatPrice(price)}</span>
                    {!available ? (
                      <span className={styles.unavailable}>Unavailable</span>
                    ) : qty > 0 && !getMenuOptionsMeta(item).needsPicker ? (
                      <div className={styles.stepper}>
                        <button
                          type="button"
                          className={styles.stepperBtn}
                          aria-label={`Decrease ${item.item}`}
                          onClick={() => void changeQty(item, qty - 1)}
                        >
                          <span className="material-symbols-outlined">remove</span>
                        </button>
                        <span className={styles.stepperQty}>{qty}</span>
                        <button
                          type="button"
                          className={styles.stepperBtn}
                          aria-label={`Increase ${item.item}`}
                          onClick={() => void changeQty(item, qty + 1)}
                        >
                          <span className="material-symbols-outlined">add</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={styles.addBtn}
                        disabled={!canOrder}
                        onClick={() => openOptionsOrAdd(item)}
                        aria-label={`Add ${item.item}`}
                      >
                        <span className="material-symbols-outlined">add</span>
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>

      {mobileFilterOpen ? (
        <MobileMenuFilterOverlay
          categories={filterCategories}
          selectedCategories={selectedCategories}
          cuisines={cuisines}
          tags={tags}
          selectedCuisines={selectedCuisines}
          selectedTags={selectedTags}
          itemType={itemType}
          priceRange={priceRange}
          priceSliderMax={priceSliderMax}
          formatPrice={formatPrice}
          sortBy={sortBy}
          showWishlist={false}
          hideSavedTab
          onWishlistChange={() => undefined}
          onCategoryToggle={handleCategoryToggle}
          onCuisineToggle={handleCuisineToggle}
          onTagToggle={handleTagToggle}
          onItemTypeToggle={handleItemTypeToggle}
          onPriceRangeChange={setPriceRange}
          onSortChange={setSortBy}
          onClear={clearMenuFilters}
          onClose={() => setMobileFilterOpen(false)}
          onApply={() => {
            void applyMenuFilter();
          }}
        />
      ) : null}

      {optionsItem && (
        <MenuItemOptionsSheet
          isOpen
          item={optionsItem}
          selections={selections}
          onChange={setSelections}
          onClose={closeOptions}
          onConfirm={confirmOptions}
          formatPrice={formatPrice}
          error={optionsError}
          confirmLabel="Add to cart"
        />
      )}
    </div>
  );
};

export default KioskMenu;
